import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Coins, AlertTriangle, Info } from 'lucide-react';
import api from '../../lib/api';
import { ProofOfReserveBadge } from '../../components/tokenization/ProofOfReserveBadge';
import { FacialVerificationModal } from '../../components/banking/FacialVerificationModal';

interface TokenInfo {
  id: string;
  name: string;
  symbol: string;
  unitPrice: number;
  availableSupply: number;
  minInvestment: number;
  maxInvestment: number | null;
  porCoverage: number;
  yieldRate: number | null;
  tradingFee: number;
}

interface AccountBalance {
  available: number;
  currency: string;
}

export function BuyToken() {
  const { tokenId } = useParams<{ tokenId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [amount, setAmount] = useState('');
  const [quantity, setQuantity] = useState('');
  const [inputMode, setInputMode] = useState<'amount' | 'quantity'>('amount');
  const [orderType, setOrderType] = useState<'market' | 'limit'>('market');
  const [limitPrice, setLimitPrice] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  // Facial verification state
  const [showFacialVerification, setShowFacialVerification] = useState(false);
  const [verificationSessionId, setVerificationSessionId] = useState<string | undefined>();
  const [verificationReason, setVerificationReason] = useState<string | undefined>();
  const [pendingOrderData, setPendingOrderData] = useState<any>(null);

  const { data: token, isLoading: tokenLoading } = useQuery({
    queryKey: ['token-buy-info', tokenId],
    queryFn: async () => {
      const res = await api.get(`/tokenization/tokens/${tokenId}/buy-info`);
      return res.data as TokenInfo;
    },
    enabled: !!tokenId,
  });

  const { data: balance } = useQuery({
    queryKey: ['account-balance'],
    queryFn: async () => {
      const res = await api.get('/banking/balance');
      return res.data as AccountBalance;
    },
  });

  const buyMutation = useMutation({
    mutationFn: async (data: { quantity: number; orderType: string; limitPrice?: number; verificationSessionId?: string }) => {
      const res = await api.post(`/tokenization/trading/orders`, {
        tokenId,
        side: 'BUY',
        type: data.orderType.toUpperCase(),
        quantity: data.quantity,
        price: data.orderType === 'limit' ? data.limitPrice : undefined,
        verificationSessionId: data.verificationSessionId,
      });
      return res.data;
    },
    onSuccess: (data) => {
      // Check if facial verification is required
      if (data.requiresVerification) {
        setVerificationSessionId(data.sessionId);
        setVerificationReason(data.reason);
        setPendingOrderData({
          quantity: calculatedQuantity,
          orderType,
          limitPrice: orderType === 'limit' ? parseCurrency(limitPrice) : undefined,
        });
        setShowFacialVerification(true);
        return;
      }
      
      queryClient.invalidateQueries({ queryKey: ['account-balance'] });
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      navigate('/banking/tokens/portfolio');
    },
  });

  // Handle facial verification completed
  const handleFacialVerificationComplete = async (sessionId: string) => {
    setShowFacialVerification(false);
    
    if (pendingOrderData) {
      // Re-send the order with the verification session ID
      buyMutation.mutate({
        ...pendingOrderData,
        verificationSessionId: sessionId,
      });
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const parseCurrency = (value: string) => {
    return Math.round(parseFloat(value.replace(/[^\d,]/g, '').replace(',', '.')) * 100) || 0;
  };

  // Calculate values based on input mode
  useEffect(() => {
    if (!token) return;
    
    if (inputMode === 'amount' && amount) {
      const amountCents = parseCurrency(amount);
      const qty = Math.floor(amountCents / token.unitPrice);
      setQuantity(qty.toString());
    } else if (inputMode === 'quantity' && quantity) {
      const qty = parseInt(quantity) || 0;
      const total = qty * token.unitPrice;
      setAmount((total / 100).toFixed(2).replace('.', ','));
    }
  }, [amount, quantity, inputMode, token]);

  const calculatedQuantity = parseInt(quantity) || 0;
  const calculatedAmount = calculatedQuantity * (token?.unitPrice || 0);
  const tradingFee = Math.round(calculatedAmount * (token?.tradingFee || 0) / 100);
  const totalCost = calculatedAmount + tradingFee;

  const canBuy = 
    calculatedQuantity > 0 &&
    totalCost <= (balance?.available || 0) &&
    calculatedAmount >= (token?.minInvestment || 0) &&
    (!token?.maxInvestment || calculatedAmount <= token.maxInvestment) &&
    calculatedQuantity <= (token?.availableSupply || 0) &&
    agreedToTerms;

  if (tokenLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!token) {
    return (
      <div className="text-center py-12">
        <Coins className="w-12 h-12 text-surface-600 mx-auto mb-3" />
        <p className="text-surface-400">Token não encontrado</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(`/banking/tokens/${tokenId}`)}
          className="p-2 hover:bg-surface-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold">Comprar {token.symbol}</h1>
          <p className="text-surface-400">{token.name}</p>
        </div>
      </div>

      {/* Token Info */}
      <div className="bg-surface-900 rounded-xl border border-surface-800 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center">
              <Coins className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <p className="font-semibold">{token.name}</p>
              <p className="text-sm text-surface-400">Preço: {formatCurrency(token.unitPrice)}</p>
            </div>
          </div>
          <ProofOfReserveBadge coverage={token.porCoverage} />
        </div>
      </div>

      {/* Order Form */}
      <div className="bg-surface-900 rounded-xl border border-surface-800 p-6 space-y-6">
        {/* Order Type */}
        <div>
          <label className="block text-sm font-medium mb-2">Tipo de Ordem</label>
          <div className="flex gap-2">
            <button
              onClick={() => setOrderType('market')}
              className={`flex-1 py-2 rounded-lg transition-colors ${
                orderType === 'market'
                  ? 'bg-primary-600 text-white'
                  : 'bg-surface-800 text-surface-400 hover:bg-surface-700'
              }`}
            >
              Mercado
            </button>
            <button
              onClick={() => setOrderType('limit')}
              className={`flex-1 py-2 rounded-lg transition-colors ${
                orderType === 'limit'
                  ? 'bg-primary-600 text-white'
                  : 'bg-surface-800 text-surface-400 hover:bg-surface-700'
              }`}
            >
              Limitada
            </button>
          </div>
        </div>

        {/* Limit Price */}
        {orderType === 'limit' && (
          <div>
            <label className="block text-sm font-medium mb-2">Preço Limite</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400">R$</span>
              <input
                type="text"
                value={limitPrice}
                onChange={(e) => setLimitPrice(e.target.value)}
                placeholder="0,00"
                className="w-full pl-10 pr-4 py-3 bg-surface-800 border border-surface-700 rounded-lg"
              />
            </div>
          </div>
        )}

        {/* Amount/Quantity Input */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium">
              {inputMode === 'amount' ? 'Valor a Investir' : 'Quantidade de Tokens'}
            </label>
            <button
              onClick={() => setInputMode(inputMode === 'amount' ? 'quantity' : 'amount')}
              className="text-sm text-primary-400 hover:underline"
            >
              {inputMode === 'amount' ? 'Inserir quantidade' : 'Inserir valor'}
            </button>
          </div>
          
          {inputMode === 'amount' ? (
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400">R$</span>
              <input
                type="text"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0,00"
                className="w-full pl-10 pr-4 py-3 bg-surface-800 border border-surface-700 rounded-lg text-lg"
              />
            </div>
          ) : (
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="0"
              min="1"
              className="w-full px-4 py-3 bg-surface-800 border border-surface-700 rounded-lg text-lg"
            />
          )}

          <div className="flex justify-between mt-2 text-sm text-surface-400">
            <span>Saldo disponível: {formatCurrency(balance?.available || 0)}</span>
            <button
              onClick={() => {
                if (balance && token) {
                  const maxQty = Math.floor(balance.available / (token.unitPrice * (1 + token.tradingFee / 100)));
                  setQuantity(maxQty.toString());
                  setInputMode('quantity');
                }
              }}
              className="text-primary-400 hover:underline"
            >
              Usar máximo
            </button>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-surface-800 rounded-lg p-4 space-y-2">
          <div className="flex justify-between">
            <span className="text-surface-400">Quantidade</span>
            <span>{calculatedQuantity.toLocaleString('pt-BR')} {token.symbol}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-surface-400">Preço unitário</span>
            <span>{formatCurrency(token.unitPrice)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-surface-400">Subtotal</span>
            <span>{formatCurrency(calculatedAmount)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-surface-400">Taxa ({token.tradingFee}%)</span>
            <span>{formatCurrency(tradingFee)}</span>
          </div>
          <div className="flex justify-between pt-2 border-t border-surface-700 font-semibold">
            <span>Total</span>
            <span>{formatCurrency(totalCost)}</span>
          </div>
        </div>

        {/* Warnings */}
        {calculatedAmount > 0 && calculatedAmount < token.minInvestment && (
          <div className="flex items-center gap-2 p-3 bg-yellow-500/20 border border-yellow-500/50 rounded-lg text-yellow-400">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm">Investimento mínimo: {formatCurrency(token.minInvestment)}</span>
          </div>
        )}

        {token.maxInvestment && calculatedAmount > token.maxInvestment && (
          <div className="flex items-center gap-2 p-3 bg-yellow-500/20 border border-yellow-500/50 rounded-lg text-yellow-400">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm">Investimento máximo: {formatCurrency(token.maxInvestment)}</span>
          </div>
        )}

        {totalCost > (balance?.available || 0) && (
          <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm">Saldo insuficiente</span>
          </div>
        )}

        {/* Yield Info */}
        {token.yieldRate && (
          <div className="flex items-center gap-2 p-3 bg-green-500/20 border border-green-500/50 rounded-lg text-green-400">
            <Info className="w-4 h-4" />
            <span className="text-sm">Este token paga {token.yieldRate.toFixed(2)}% a.a. de rendimento</span>
          </div>
        )}

        {/* Terms */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            className="mt-1"
          />
          <span className="text-sm text-surface-400">
            Li e concordo com os termos de investimento e entendo os riscos envolvidos na aquisição de tokens.
          </span>
        </label>

        {/* Submit */}
        <button
          onClick={() => buyMutation.mutate({
            quantity: calculatedQuantity,
            orderType,
            limitPrice: orderType === 'limit' ? parseCurrency(limitPrice) : undefined,
          })}
          disabled={!canBuy || buyMutation.isPending}
          className="w-full py-3 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors font-semibold"
        >
          {buyMutation.isPending ? 'Processando...' : `Comprar ${calculatedQuantity} ${token.symbol}`}
        </button>
      </div>

      {/* Facial Verification Modal */}
      <FacialVerificationModal
        isOpen={showFacialVerification}
        onClose={() => {
          setShowFacialVerification(false);
          setPendingOrderData(null);
        }}
        onVerified={handleFacialVerificationComplete}
        operationType="TOKEN_PURCHASE"
        operationValue={totalCost}
        sessionId={verificationSessionId}
        reason={verificationReason}
      />
    </div>
  );
}

export default BuyToken;
